import { Person } from 'model/person';
import { Project } from 'model/project';
import { ProjectPerson, ProjectPersonInterval } from 'model/project-person';
import { deepCopy } from 'utils/deepcopy';
import { loadDate, serializeDate } from 'utils/date';
import { ProjectExpense } from 'model/project-expense';
import {
  BillRate, BillRateType, Discount, DiscountType, PurchaseOrderNumber, RateInterval,
  RateType,
} from 'model/rates';
import { Bookmark } from 'model/bookmark';
import { FilteredResourcePermission, PermissionsTypes } from 'model/role-permissions';
import { SkillPerson } from 'model/skill';
import { Game, PersonGame } from 'model/game';
import { ProjectRoleInterval } from 'model/project-role';
import { sortProjectPersonIntervalEntriesChronologically } from './project-person';

/* Utils */

export const serializeBoolean = (value: boolean) => (value ? '1' : '0');

export const loadPurchaseOrderNumber = (interval: any): PurchaseOrderNumber => {
  const intervalLoaded: PurchaseOrderNumber = deepCopy(interval);
  intervalLoaded.start_date = loadDate(interval.start_date) as Date;
  intervalLoaded.end_date = loadDate(interval.end_date) as Date | null;

  return intervalLoaded;
};

export const serializePurchaseOrderNumber = (interval: PurchaseOrderNumber): any => {
  const serializedRateInterval: any = deepCopy(interval);

  serializedRateInterval.start_date = serializeDate(interval.start_date);
  serializedRateInterval.end_date = serializeDate(interval.end_date);

  return serializedRateInterval;
};

/* Person */

export function loadPerson(person: any): Person {
  const loadedPerson: Person = deepCopy(person);
  loadedPerson.start_date = loadDate(person.start_date) as Date;
  loadedPerson.left_date = loadDate(person.left_date) as Date | null;
  loadedPerson.city = person.city ? person.city : '';
  loadedPerson.country = person.country ? person.country : '';
  loadedPerson.timezone = person.timezone ? person.timezone : '';
  if (loadedPerson.timezone !== '') {
    loadedPerson.timezone_name = person.timezone_name ? person.timezone_name : loadedPerson.timezone;
  } else {
    loadedPerson.timezone_name = person.timezone_name ? person.timezone_name : '';
  }
  return loadedPerson;
}

export function loadPeople(people: any[]): Person[] {
  const peopleFormatted = people.map((person) => loadPerson(person));
  return peopleFormatted;
}

export function serializePerson(person: Person): any {
  const serializedPerson: any = deepCopy(person);
  serializedPerson.start_date = serializeDate(person.start_date);
  serializedPerson.left_date = serializeDate(person.left_date);
  return serializedPerson;
}

/* Project */

export function loadProject(project: any): Project {
  const projectFormatted: Project = deepCopy(project);

  if (projectFormatted.is_hidden_pitch) {
    // Hidden-pitch marker from project_read — only { id, is_hidden_pitch }
    // are populated. Skip date parsing and PO normalisation because the
    // fields aren't present and parsing undefined dates would produce
    // Invalid Date objects the UI would try to render.
    return projectFormatted;
  }

  if (Object.keys(projectFormatted).length === 2) {
    // Api is returning id and name only - skip the data/billing process
    return projectFormatted;
  }

  projectFormatted.start_date = loadDate(project.start_date) as Date;
  projectFormatted.end_date = loadDate(project.end_date) as Date | null;
  projectFormatted.last_invoice_date = loadDate(project.last_invoice_date) as Date | null;
  projectFormatted.first_invoice_date = loadDate(project.first_invoice_date) as Date | null;
  projectFormatted.due_date = loadDate(project.due_date) as Date | null;
  projectFormatted.likely_end_date = loadDate(project.likely_end_date) as Date | null;

  if (project.purchase_order_numbers === undefined || project.purchase_order_numbers.length === 0) {
    projectFormatted.purchase_order_numbers = [];
    if (projectFormatted.purchase_order_number) {
      projectFormatted.purchase_order_numbers.push({
        id: crypto.randomUUID(),
        start_date: projectFormatted.start_date,
        end_date: projectFormatted.end_date,
        vat: projectFormatted.vat,
        number: projectFormatted.purchase_order_number,
      });
    }
  } else {
    projectFormatted.purchase_order_numbers = project.purchase_order_numbers.map((po: PurchaseOrderNumber) => loadPurchaseOrderNumber(po));
  }

  return projectFormatted;
}

export function loadProjects(apiProjects: any[]): Project[] {
  const projectsFormatted = apiProjects.map((project) => loadProject(project));
  return projectsFormatted;
}

export function serializeProject(project: Project): any {
  const serializedProject: any = deepCopy(project);
  serializedProject.start_date = serializeDate(project.start_date);
  serializedProject.end_date = serializeDate(project.end_date);
  serializedProject.due_date = serializeDate(project.due_date ?? null);
  serializedProject.likely_end_date = serializeDate(project.likely_end_date ?? null);
  serializedProject.purchase_order_numbers = project.purchase_order_numbers.map((po: PurchaseOrderNumber) => serializePurchaseOrderNumber(po));

  return serializedProject;
}

/* Project-person */

export function serializeProjectPerson(projectPerson: ProjectPerson): any {
  const serializedProjectPerson: any = deepCopy(projectPerson);
  serializedProjectPerson.valid_from = serializeDate(projectPerson.valid_from);
  serializedProjectPerson.end_date = serializeDate((projectPerson as any).end_date);

  return serializedProjectPerson;
}

export function loadProjectPerson(projectPerson: any): ProjectPerson {
  const loadedProjectPerson: ProjectPerson = deepCopy(projectPerson);
  loadedProjectPerson.valid_from = loadDate(projectPerson.valid_from) as Date;

  return loadedProjectPerson;
}

export function loadProjectPersons(projectPersons: any[]): ProjectPerson[] {
  const projectPersonsFormatted = projectPersons.map(
    (projectPerson) => loadProjectPerson(projectPerson),
  );
  return projectPersonsFormatted;
}

/* Project Role Interval */
export function serializeProjectRoleInterval(projectRoleInterval: ProjectRoleInterval): any {
  const serializedProjectRoleInterval: any = deepCopy(projectRoleInterval);
  serializedProjectRoleInterval.valid_from = serializeDate(projectRoleInterval.valid_from);
  serializedProjectRoleInterval.end_date = serializeDate(projectRoleInterval.end_date);

  return serializedProjectRoleInterval;
}

export function loadProjectRoleInterval(projectRoleInterval: any): ProjectRoleInterval {
  const loadedProjectRoleInterval: ProjectRoleInterval = deepCopy(projectRoleInterval);
  loadedProjectRoleInterval.valid_from = loadDate(projectRoleInterval.valid_from) as Date;
  loadedProjectRoleInterval.end_date = projectRoleInterval.end_date === null ? undefined : loadDate(projectRoleInterval.end_date) as Date;

  return loadedProjectRoleInterval;
}

export function loadProjectRoleIntervals(projectRoleIntervals: any[]): ProjectRoleInterval[] {
  const projectRoleIntervalsFormatted = projectRoleIntervals.map(
    (projectRoleInterval) => loadProjectRoleInterval(projectRoleInterval),
  );
  return projectRoleIntervalsFormatted;
}

/* Project expense */

export function loadExpense(expense: any): ProjectExpense {
  const loadedExpense: ProjectExpense = deepCopy(expense);
  loadedExpense.date = loadDate(expense.date) as Date;

  return loadedExpense;
}

export function loadExpenses(expenses: any[]): ProjectExpense[] {
  const loadedExpenses = expenses.map((expense) => loadExpense(expense));
  return loadedExpenses;
}

export function serializeExpense(expense: ProjectExpense): any {
  const serializedExpense: any = deepCopy(expense);
  serializedExpense.date = serializeDate(expense.date);
  return serializedExpense;
}

/* Bookmark */

export function loadBookmark(bookmark: any): Bookmark {
  const loadedBookmark: Bookmark = deepCopy(bookmark);
  loadedBookmark.created_at = loadDate(bookmark.created_at) as Date;

  return loadedBookmark;
}

export function loadBookmarks(bookmarks: any[]): Bookmark[] {
  const loadedBookmark = bookmarks.map((bookmark) => loadBookmark(bookmark));
  return loadedBookmark;
}

export function serializeBookmark(bookmark: Bookmark): any {
  const serializedBookmark: any = deepCopy(bookmark);
  serializedBookmark.created_at = serializeDate(bookmark.created_at);
  return serializedBookmark;
}

/* Permissions */

export function loadPermission(permission: any): PermissionsTypes {
  if (typeof permission !== 'string') {
    throw new Error('Invalid permission type');
  }

  const mapping: Record<string, PermissionsTypes> = {
    Read: PermissionsTypes.Read,
    ReadWrite: PermissionsTypes.ReadWrite,
    Deny: PermissionsTypes.Deny,
    Function: PermissionsTypes.Function,
  };

  return mapping[permission] ?? PermissionsTypes.Deny;
}

export function loadFilteredResourcePermission(permission: any): FilteredResourcePermission[] {
  if (!Array.isArray(permission)) {
    throw new Error('Invalid permission format: Expected an array');
  }

  const mapping: Record<string, PermissionsTypes> = {
    Read: PermissionsTypes.Read,
    ReadWrite: PermissionsTypes.ReadWrite,
    Deny: PermissionsTypes.Deny,
  };

  return permission.map((entry) => {
    if (!entry.id || !entry.permission || typeof entry.id !== 'string') {
      throw new Error('Invalid entry format in permission list');
    }

    return {
      id: entry.id as string,
      permission: mapping[entry.permission] || PermissionsTypes.Deny,
    };
  });
}
/* Skill-Person */

export function loadSkillPerson(skillPerson: any): SkillPerson {
  const loadedSkillPerson: SkillPerson = deepCopy(skillPerson);
  loadedSkillPerson.valid_from = loadDate(skillPerson.valid_from) as Date;
  return loadedSkillPerson;
}

export function loadSkillPersons(skillPersons: any[]): SkillPerson[] {
  return skillPersons.map(
    (skillPerson) => loadSkillPerson(skillPerson),
  );
}

export function serializeSkillPerson(skillPerson: Partial<SkillPerson>): any {
  const serializedSkillPerson: any = deepCopy(skillPerson);
  serializedSkillPerson.valid_from = serializeDate(skillPerson.valid_from);
  return serializedSkillPerson;
}

/* Game */

export function loadGame(game: any): Game {
  const loadedGame: Game = deepCopy(game);
  loadedGame.release_date = game.release_date ? loadDate(game.release_date) as Date : null;
  return loadedGame;
}

export function loadGames(games: any[]): Game[] {
  return games.map((game) => loadGame(game));
}

/* Person-Game */

export function loadPersonGame(personGame: any): PersonGame {
  const loadedPersonGame: PersonGame = deepCopy(personGame);
  loadedPersonGame.start_date = loadDate(personGame.start_date) as Date;
  loadedPersonGame.end_date = personGame.end_date ? loadDate(personGame.end_date) as Date : null;
  return loadedPersonGame;
}

export function loadPersonGames(personGames: any[]): PersonGame[] {
  return personGames.map(
    (personGame) => loadPersonGame(personGame),
  );
}

export function serializePersonGame(personGame: Partial<PersonGame>): any {
  const serializedPersonGame: any = deepCopy(personGame);
  serializedPersonGame.start_date = serializeDate(personGame.start_date);
  serializedPersonGame.end_date = serializeDate(personGame.end_date);
  return serializedPersonGame;
}

/* Timelines */
export function loadProjectPersonInterval(interval: any): ProjectPersonInterval {
  const formattedInterval: any = deepCopy(interval);
  formattedInterval.valid_from = loadDate(interval.valid_from) as Date;
  formattedInterval.end_date = interval.end_date === null ? undefined : loadDate(interval.end_date) as Date;

  return formattedInterval;
}

export function loadTimeline(tm: any): ProjectPersonInterval[] {
  return tm.timeline.flatMap((interval: any) => loadProjectPersonInterval(interval));
}

export function loadTimelineProjectPersonIntervals(timelines: any[]): ProjectPersonInterval[] {
  const timelineFormatted = timelines.map((timeline) => loadTimeline(timeline));
  let ppInterval = timelineFormatted.flat();
  ppInterval = sortProjectPersonIntervalEntriesChronologically(ppInterval, false);
  return ppInterval;
}

/* Rate Intervals */

export const loadRateInterval = (interval: any): BillRate | Discount | RateInterval => {
  const intervalLoaded: RateInterval = deepCopy(interval);
  intervalLoaded.start_date = loadDate(interval.start_date) as Date;
  intervalLoaded.end_date = loadDate(interval.end_date) as Date | null;
  intervalLoaded.amount = Number.isNaN(interval.amount) ? '0' : interval.amount;

  if (intervalLoaded.type === RateType.BILL_RATE) {
    return {
      ...intervalLoaded,
      name: interval.name || '',
      br_type: interval.br_type || BillRateType.MONTHLY,
    } as BillRate;
  } if (intervalLoaded.type === RateType.DISCOUNT) {
    return {
      ...intervalLoaded,
      discount_type: interval.discount_type || DiscountType.PERCENTAGE,
      description: interval.description || '',
    } as Discount;
  }

  return intervalLoaded;
};

export function loadRateIntervals(apiIntervals: any[]): [BillRate[], Discount[]] {
  const billRates: BillRate[] = [];
  const discounts: Discount[] = [];

  apiIntervals.forEach((interval) => {
    const loadedInterval = loadRateInterval(interval);
    if (loadedInterval.type === RateType.BILL_RATE) {
      billRates.push(loadedInterval as BillRate);
    } else if (loadedInterval.type === RateType.DISCOUNT) {
      discounts.push(loadedInterval as Discount);
    }
  });

  return [billRates, discounts];
}

export const serializeRateInterval = (interval: BillRate | Discount): any => {
  const serializedRateInterval: any = deepCopy(interval);

  serializedRateInterval.start_date = serializeDate(interval.start_date);
  serializedRateInterval.end_date = serializeDate(interval.end_date);

  return serializedRateInterval;
};
